<template>
  <div>
    <!-- 批注标记列表 -->
    <template v-if="annotations && annotations.length > 0">
      <div 
        v-for="annotation in annotations" 
        :key="annotation.id"
        class="annotation-marker"
        :data-annotation-id="annotation.id"
        @click="handleAnnotationClick(annotation)"
        @touchstart="recordTouchStart($event)"
        @touchend.prevent="handleAnnotationTouch($event, annotation)"
      >
        <!-- 图钉锚点：圆心对齐场景坐标 -->
        <div 
          v-if="enableDrag"
          class="marker-pin" 
          @mousedown.stop="handlePinMouseDown($event, annotation)"
          @touchstart.stop="handlePinTouchStart($event, annotation)"
          title="拖拽移动位置"
        >
          📌
        </div>
        <div 
          v-else
          class="marker-pin-static"
          title="批注"
        >
          📌
        </div>

        <!-- 批注卡片 -->
        <div class="annotation-card">
          <!-- 批注内容 -->
          <div class="annotation-info">
            <!-- 用户信息 -->
            <div class="annotation-user">
              <BaseNameBoard :uid="annotation.user_id" />
            </div>
            
            <!-- 评论内容预览 -->
            <div class="annotation-content-preview">
              {{ annotation.content }}
            </div>
            
            <!-- 底部信息栏 -->
            <div class="annotation-footer">
              <span class="annotation-time">{{ formatTimeShort(annotation.timestamp) }}</span>
              <span v-if="annotation.replies && annotation.replies.length > 0" class="annotation-replies">
                💬 {{ annotation.replies.length }} 条回复
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- 批注详情弹窗 -->
    <div v-if="selectedAnnotation" class="annotation-modal" @click="closeDetail">
      <div class="annotation-modal-content" @click.stop>
        <div class="annotation-modal-header">
          <h3>批注详情</h3>
          <button class="close-btn" @click="closeDetail">×</button>
        </div>
        
        <div class="annotation-modal-body">
          <!-- 主评论 -->
          <div class="comment-item main-comment">
            <div class="comment-header">
              <span class="comment-user">
                <BaseNameBoard :uid="selectedAnnotation.user_id" />
              </span>
              <span class="comment-time">{{ formatTime(selectedAnnotation.timestamp) }}</span>
            </div>
            <p class="comment-text">{{ selectedAnnotation.content }}</p>
            <div class="comment-actions" v-if="showActions">
              <button 
                v-if="canReply" 
                class="btn-reply" 
                @click="startReply"
              >
                💬 回复
              </button>
              <button 
                v-if="canDelete(selectedAnnotation)" 
                class="btn-delete-comment" 
                @click="$emit('delete-annotation', selectedAnnotation.id)"
              >
                🗑️ 删除
              </button>
            </div>
          </div>

          <!-- 回复列表 -->
          <div v-if="selectedAnnotation.replies && selectedAnnotation.replies.length > 0" class="replies-section">
            <div class="replies-header">
              <span class="replies-count">{{ selectedAnnotation.replies.length }} 条回复</span>
              <button 
                v-if="selectedAnnotation.replies.length > 3 && !showAllReplies" 
                class="btn-show-more"
                @click="showAllReplies = true"
              >
                展开全部
              </button>
              <button 
                v-if="showAllReplies && selectedAnnotation.replies.length > 3" 
                class="btn-show-more"
                @click="showAllReplies = false"
              >
                收起
              </button>
            </div>
            <div 
              v-for="reply in displayedReplies" 
              :key="reply.id"
              class="comment-item reply-comment"
            >
              <div class="comment-header">
                <span class="comment-user">
                  <BaseNameBoard :uid="reply.user_id" />
                </span>
                <span class="comment-time">{{ formatTime(reply.timestamp) }}</span>
              </div>
              <p class="comment-text">{{ reply.content }}</p>
              <div class="comment-actions" v-if="showActions">
                <button 
                  v-if="canDelete(reply)" 
                  class="btn-delete-comment" 
                  @click="$emit('delete-reply', reply.id)"
                >
                  🗑️ 删除
                </button>
              </div>
            </div>
          </div>

          <!-- 回复输入框 -->
          <div v-if="replyingTo === selectedAnnotation.id" class="reply-input-section">
            <textarea 
              v-model="replyText" 
              placeholder="输入回复内容..."
              class="reply-input"
              rows="3"
              ref="replyInput"
            ></textarea>
            <div class="reply-input-actions">
              <button class="btn-cancel" @click="cancelReply">取消</button>
              <button class="btn-save" @click="saveReply">发送</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UAVAnnotation',
  props: {
    // 批注列表
    annotations: {
      type: Array,
      default: () => [],
    },
    // 当前用户ID
    currentUserId: {
      type: [String, Number],
      default: null,
    },
    // 项目创建人ID（可删除所有批注）
    projectCreatorId: {
      type: [String, Number],
      default: null,
    },
    // 是否可以拖拽
    enableDrag: {
      type: Boolean,
      default: false,
    },
    // 是否显示操作按钮
    showActions: {
      type: Boolean,
      default: true,
    },
    // 是否可以回复
    canReply: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selectedAnnotation: null,
      showAllReplies: false,
      replyingTo: null,
      replyText: '',
      touchStartTime: 0,
      touchStartX: 0,
      touchStartY: 0,
      suppressClickUntil: 0,
    };
  },
  computed: {
    displayedReplies() {
      if (!this.selectedAnnotation || !this.selectedAnnotation.replies) {
        return [];
      }
      if (this.showAllReplies || this.selectedAnnotation.replies.length <= 3) {
        return this.selectedAnnotation.replies;
      }
      // 只显示最近3条
      return this.selectedAnnotation.replies.slice(-3);
    },
  },
  methods: {
    recordTouchStart(event) {
      // 记录触摸开始时间和位置
      this.touchStartTime = Date.now();
      if (event.touches && event.touches.length > 0) {
        const touch = event.touches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
      }
    },

    handleAnnotationClick(annotation) {
      if (this.shouldSuppressClick()) return;
      this.selectedAnnotation = annotation;
      this.showAllReplies = false;
      this.replyingTo = null;
      this.replyText = '';
      this.$emit('annotation-click', annotation);
    },

    handleAnnotationTouch(event, annotation) {
      if (this.shouldSuppressClick()) return;
      const touchEndTime = Date.now();
      const touchDuration = touchEndTime - this.touchStartTime;
      
      if (touchDuration < 500) {
        this.selectedAnnotation = annotation;
        this.showAllReplies = false;
        this.replyingTo = null;
        this.replyText = '';
        this.$emit('annotation-click', annotation);
      }
    },

    shouldSuppressClick() {
      return Date.now() < this.suppressClickUntil;
    },

    suppressClickAfterDrag() {
      this.suppressClickUntil = Date.now() + 300;
    },

    handlePinMouseDown(event, annotation) {
      // PC端拖拽开始（.stop已经阻止冒泡，不会触发父元素的click）
      this.$emit('drag-start', event, annotation);
    },

    handlePinTouchStart(event, annotation) {
      // 移动端触摸图钉（.stop已经阻止冒泡）
      this.touchStartTime = Date.now();
      const touch = event.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      
      // 如果启用拖拽，触发拖拽事件
      if (this.enableDrag) {
        this.$emit('drag-start', event, annotation);
      }
    },

    closeDetail() {
      this.selectedAnnotation = null;
      this.showAllReplies = false;
      this.replyingTo = null;
      this.replyText = '';
    },

    canDelete(comment) {
      if (!this.currentUserId) return false;
      const commentUserId = comment.created_by || comment.user_id;
      if (commentUserId === this.currentUserId) return true;
      return this.projectCreatorId && String(this.projectCreatorId) === String(this.currentUserId);
    },

    startReply() {
      this.replyingTo = this.selectedAnnotation.id;
      this.replyText = '';
      this.$nextTick(() => {
        if (this.$refs.replyInput) {
          this.$refs.replyInput.focus();
        }
      });
    },

    cancelReply() {
      this.replyingTo = null;
      this.replyText = '';
    },

    saveReply() {
      if (!this.replyText.trim()) {
        alert('请输入回复内容');
        return;
      }
      this.$emit('save-reply', {
        annotationId: this.selectedAnnotation.id,
        content: this.replyText,
      });
      this.replyText = '';
      this.replyingTo = null;
    },

    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    formatTimeShort(timestamp) {
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return '刚刚';
      if (minutes < 60) return `${minutes}分钟前`;
      if (hours < 24) return `${hours}小时前`;
      if (days < 7) return `${days}天前`;
      
      return date.toLocaleDateString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
/* 批注标记 */
.annotation-marker {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 5;
  pointer-events: auto;
  cursor: pointer;
  visibility: hidden;
  will-change: transform;
  transition: opacity 0.2s ease;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.annotation-marker:hover {
  z-index: 100;
}

/* 批注卡片 */
.annotation-card {
  background: rgba(255, 255, 255, 0.98);
  border-radius: 12px;
  padding: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 280px;
  min-width: 200px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(102, 126, 234, 0.2);
  transition: all 0.3s ease;
}

.annotation-card:hover {
  box-shadow: 0 6px 30px rgba(102, 126, 234, 0.3);
  border-color: rgba(102, 126, 234, 0.5);
}

/* 图钉图标（可拖拽） */
.marker-pin,
.marker-pin-static {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.marker-pin {
  cursor: move;
  animation: pulse-pin 2s ease-in-out infinite;
}

.marker-pin-static {
  animation: pulse-pin 2s ease-in-out infinite;
}

.marker-pin:hover {
  transform: scale(1.1);
  animation: none;
}

.marker-pin:active {
  cursor: grabbing;
  transform: scale(1.15);
  animation: none;
}

@keyframes pulse-pin {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4), 0 0 0 0 rgba(102, 126, 234, 0.7);
  }
  50% {
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4), 0 0 0 8px rgba(102, 126, 234, 0);
  }
}

/* 批注信息区 */
.annotation-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* 用户信息 */
.annotation-user {
  display: flex;
  align-items: center;
}

/* 评论内容预览 */
.annotation-content-preview {
  font-size: 13px;
  color: #333;
  line-height: 1.5;
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  max-height: 40px;
}

/* 底部信息栏 */
.annotation-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  color: #999;
  margin-top: 2px;
}

.annotation-time {
  color: #999;
}

.annotation-replies {
  color: #667eea;
  font-weight: 500;
  white-space: nowrap;
}

/* 批注弹窗 */
.annotation-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.annotation-modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.annotation-modal-header {
  padding: 16px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.annotation-modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.annotation-modal-body {
  padding: 20px;
  max-height: 500px;
  overflow-y: auto;
}

.annotation-modal-body::-webkit-scrollbar {
  width: 6px;
}

.annotation-modal-body::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.annotation-modal-body::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 3px;
}

.annotation-modal-body::-webkit-scrollbar-thumb:hover {
  background: #ccc;
}

/* 评论项 */
.comment-item {
  margin-bottom: 16px;
  padding-bottom: 16px;
}

.main-comment {
  border-bottom: 2px solid #e0e0e0;
}

.reply-comment {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  border-left: 3px solid #667eea;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.comment-user {
  font-weight: 600;
  color: #667eea;
  font-size: 14px;
}

.comment-time {
  font-size: 12px;
  color: #999;
}

.comment-text {
  margin: 0 0 12px 0;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  white-space: pre-wrap;
  word-break: break-word;
}

.comment-actions {
  display: flex;
  gap: 8px;
}

.btn-reply,
.btn-delete-comment {
  padding: 4px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-reply {
  background: #e3f2fd;
  color: #1976d2;
}

.btn-reply:hover {
  background: #bbdefb;
}

.btn-delete-comment {
  background: #ffebee;
  color: #c62828;
}

.btn-delete-comment:hover {
  background: #ffcdd2;
}

/* 回复部分 */
.replies-section {
  margin-top: 16px;
}

.replies-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.replies-count {
  font-size: 13px;
  font-weight: 600;
  color: #666;
}

.btn-show-more {
  padding: 4px 12px;
  background: #f0f0f0;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-show-more:hover {
  background: #e0e0e0;
}

/* 回复输入 */
.reply-input-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.reply-input {
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.3s;
  box-sizing: border-box;
  margin-bottom: 12px;
}

.reply-input:focus {
  outline: none;
  border-color: #667eea;
}

.reply-input-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.btn-cancel,
.btn-save {
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-cancel {
  background: #e0e0e0;
  color: #666;
}

.btn-cancel:hover {
  background: #d0d0d0;
}

.btn-save {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
}

.btn-save:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .annotation-marker {
    /* 增加触摸区域 */
    padding: 5px;
  }

  .annotation-card {
    max-width: 220px;
    min-width: 180px;
    padding: 8px;
    /* 确保触摸友好 */
    touch-action: manipulation;
  }

  .marker-pin,
  .marker-pin-static {
    width: 32px;
    height: 32px;
    font-size: 14px;
    /* 增加点击区域 */
    min-width: 44px;
    min-height: 44px;
  }

  .annotation-content-preview {
    font-size: 12px;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }

  .annotation-footer {
    font-size: 10px;
  }

  .annotation-modal-content {
    width: 95%;
  }

  /* 移动端弹窗优化 */
  .annotation-modal {
    -webkit-overflow-scrolling: touch;
  }
}
</style>

